import { Button, Center, Flex, Loader, Stack, Text, ActionIcon, Paper, Grid } from '@mantine/core';
import {
  IconBrandSnowflake,
  IconMinus,
  IconPlus,
  IconPower,
  IconPropeller,
  IconSun,
  IconPlugConnected,
} from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import panic from 'panic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DASHBOARD_PATH } from 'routes/paths';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { AirConditionerIcon } from 'components/icons/AirConditionerIcon';
import { AirVentIcon } from 'components/icons/AirVentIcon';

/** Maps Daikin operationMode to local mode type */
function toLocalMode(operationMode: string | null): 'cool' | 'heat' | 'fan' {
  if (operationMode === 'heating') return 'heat';
  if (operationMode === 'fanOnly') return 'fan';
  return 'cool';
}

/** Maps local mode type to Daikin operationMode */
function toDaikinMode(mode: 'cool' | 'heat' | 'fan'): string {
  if (mode === 'heat') return 'heating';
  if (mode === 'fan') return 'fanOnly';
  return 'cooling';
}

/**
 * AirConditionerDetail
 * @param param0
 * @returns
 */
export function AirConditionerDetailImpl({ airConditionerId }: { airConditionerId: number }) {
  const { getAction } = useApi();
  const { _t } = useLang();

  const [authLoading, setAuthLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const [loading, setLoading] = useState(true);
  const [temperature, setTemperature] = useState(25);
  const [roomTemperature, setRoomTemperature] = useState<number | null>(null);
  const [isOn, setIsOn] = useState(false);
  const [mode, setMode] = useState<'cool' | 'heat' | 'fan'>('cool');
  const [fanSpeed, setFanSpeed] = useState<'low' | 'medium' | 'high'>('low');
  const [updatingCharacteristic, setUpdatingCharacteristic] = useState<string | null>(null);
  const isUpdating = updatingCharacteristic !== null;

  /** Sends a characteristic update to the backend/Daikin and tracks loading state */
  const setCharacteristic = useCallback(
    /**
     * @param characteristicName - Daikin characteristic name
     * @param value - new value
     */
    (characteristicName: string, value: unknown) => {
      if (updatingCharacteristic !== null) return;
      setUpdatingCharacteristic(characteristicName);
      const setAcCharacteristic = getAction('SetAirConditionerCharacteristic');
      setAcCharacteristic({ parameters: { airConditionerId, characteristicName }, payload: { value } })
        .catch(panic)
        .finally(() => setUpdatingCharacteristic(null));
    },
    [airConditionerId, getAction, updatingCharacteristic]
  );

  /** Checks whether the current user has Daikin connected */
  useEffect(() => {
    /** Fetches Daikin auth status and updates isConnected state. */
    const checkStatus = () => {
      const getDaikinAuthStatus = getAction('GetDaikinAuthStatus');
      getDaikinAuthStatus({})
        .then(({ connected }) => {
          setIsConnected(connected);
        })
        .catch(panic)
        .finally(() => setAuthLoading(false));
    };

    checkStatus();

    // Re-check when user returns to this tab after completing OAuth in a new tab
    /** Re-checks Daikin connection status when the tab becomes visible. */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [getAction]);

  /**
   * Handles connecting to Daikin — requests the auth URL from backend and opens it in a new tab.
   */
  const handleConnect = useCallback(() => {
    setIsConnecting(true);
    const startDaikinAuth = getAction('StartDaikinAuth');
    startDaikinAuth({ payload: {} })
      .then(({ url }) => {
        window.location.href = url;
      })
      .catch(panic)
      .finally(() => setIsConnecting(false));
  }, [getAction]);

  /** Loads current air conditioner state from API — only runs after Daikin connection is confirmed */
  useEffect(() => {
    if (airConditionerId && isConnected) {
      const getAirConditioner = getAction('GetAirConditioner');
      getAirConditioner({ parameters: { airConditionerId } })
        .then(({ airConditioner }) => {
          const status = airConditioner.status;
          setIsOn(status.isOn);
          setTemperature(status.setpointTemperature ?? 25);
          setRoomTemperature(status.roomTemperature);
          setMode(toLocalMode(status.operationMode));
        })
        .catch(panic)
        .finally(() => setLoading(false));
    }
  }, [airConditionerId, getAction, isConnected]);

  /**
   * IncreaseTemp
   */
  const increaseTemp = () => {
    const newTemp = Math.min(temperature + 1, 30);
    setTemperature(newTemp);
    setCharacteristic('temperatureControl', {
      operationModes: { [toDaikinMode(mode)]: { setpoints: { roomTemperature: { value: newTemp } } } },
    });
  };

  /**
   * DecreaseTemp
   */
  const decreaseTemp = () => {
    const newTemp = Math.max(temperature - 1, 16);
    setTemperature(newTemp);
    setCharacteristic('temperatureControl', {
      operationModes: { [toDaikinMode(mode)]: { setpoints: { roomTemperature: { value: newTemp } } } },
    });
  };

  const minTemp = 16;
  const maxTemp = 30;

  const radius = 110;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.75;

  const percent = (temperature - minTemp) / (maxTemp - minTemp);
  const dashOffset = arcLength * (1 - percent);

  const startAngle = 135;
  const sweepAngle = 270;

  const angle = startAngle + percent * sweepAngle;
  const rad = (angle * Math.PI) / 180;

  const knobX = 130 + radius * Math.cos(rad);
  const knobY = 130 + radius * Math.sin(rad);

  const startAngleRad = (135 * Math.PI) / 180;
  const knobStartX = 130 + radius * Math.cos(startAngleRad);
  const knobStartY = 130 + radius * Math.sin(startAngleRad);

  if (authLoading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Flex direction="column" align="center" gap="1rem">
          <Loader size="lg" color="#551FFF" />
        </Flex>
      </Center>
    );
  }

  if (!isConnected) {
    return (
      <Stack bg="gray.0" py={20} px={16} mih="100vh" align="center" gap={4}>
        <Paper py="20px" px="16px" w="100%" bg="white" style={{ borderRadius: '10px' }}>
          <Stack align="center" gap={16}>
            <IconPlugConnected size={48} color="#551FFF" />
            <Text fw={700} size="xl" ta="center">
              {_t('Connect air conditioning to Daikin')}
            </Text>
            <Text c="dimmed" ta="center" size="sm">
              {_t('Click the button and sign in to your Daikin Onecta account.')}
            </Text>
            <Button
              fullWidth
              size="lg"
              radius="xl"
              leftSection={<IconPlugConnected size={20} />}
              loading={isConnecting}
              onClick={handleConnect}
              style={{ backgroundColor: '#551FFF', color: 'white' }}
            >
              {_t('Connect with Daikin')}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Flex direction="column" align="center" gap="1rem">
          <Loader size="lg" color="#551FFF" />
        </Flex>
      </Center>
    );
  }

  return (
    <Stack bg="gray.0" py={20} px={16} mih="100vh" align="center" gap={4}>
      <Paper py="20px" px="16px" w="100%" bg="white" style={{ borderRadius: '10px 10px 0px 0px' }}>
        {/* STATUS */}
        <Text ta="center" mb={10} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="6.00022" cy="5.99973" r="2.72727" fill="#0D0E14" />
            <circle cx="6" cy="6" r="5.5" stroke="#8691A1" />
          </svg>
          {isOn ? _t('Active') : _t('Off')}
        </Text>

        {/* DIAL */}
        <Stack align="center">
          <div
            style={{
              width: 260,
              height: 260,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="260" height="260" style={{ position: 'absolute' }}>
              <defs>
                <linearGradient id="circleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFBFB0" />
                  <stop offset="50%" stopColor="#FFBFB0" />
                  <stop offset="100%" stopColor="#B49DFF" />
                </linearGradient>

                <filter id="circleShadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="rgba(13, 14, 20, 0.10)" />
                </filter>
              </defs>
              {/* background arc */}
              <circle
                cx="130"
                cy="130"
                r={radius}
                stroke="#E9EBEC"
                strokeWidth="26"
                fill="none"
                strokeDasharray={`${arcLength} ${circumference}`}
                transform="rotate(135 130 130)"
                strokeLinecap="round"
              />

              {/* progress arc */}
              {isOn && (
                <circle
                  cx="130"
                  cy="130"
                  r={radius}
                  stroke="url(#circleGradient)"
                  strokeWidth="26"
                  fill="none"
                  strokeDasharray={`${arcLength} ${circumference}`}
                  strokeDashoffset={dashOffset}
                  transform="rotate(135 130 130)"
                  strokeLinecap="round"
                />
              )}

              {/* knob */}
              <circle
                cx={isOn ? knobX : knobStartX}
                cy={isOn ? knobY : knobStartY}
                r="18"
                fill={isOn ? 'black' : '#AFB5C1'}
                stroke="white"
                strokeWidth="10"
                filter="url(#circleShadow)"
              />
            </svg>

            <Stack align="center" gap={4}>
              <Flex align="center" gap="8px" style={{ height: '105px', marginTop: '55px' }}>
                {mode !== 'fan' && <AirConditionerIcon />}
                {isOn && mode !== 'fan' && (
                  <>
                    <span style={{ fontSize: '56px', fontFamily: 'SF Pro Display', fontWeight: 600 }}>
                      {temperature}
                    </span>
                    <span style={{ fontSize: '32px', fontFamily: 'SF Pro Display', fontWeight: 600 }}>°C</span>
                  </>
                )}
                {isOn && mode === 'fan' && <AirVentIcon />}
              </Flex>
              {/* Aktuálna teplota */}
              <Text size="sm" c="dimmed">
                {_t('Current temperature')}
              </Text>

              <Text size="xl" fw={700}>
                {roomTemperature != null ? `${roomTemperature}°C` : '—'}
              </Text>
            </Stack>
          </div>
        </Stack>

        {/* CONTROLS */}
        <Flex justify="center" align="center" gap={10}>
          <ActionIcon size={60} radius="xl" variant="secondary" onClick={decreaseTemp} disabled={!isOn || isUpdating}>
            <IconMinus size={28} />
          </ActionIcon>

          <Button
            radius="xl"
            size="xl"
            variant="secondary"
            leftSection={<IconPower size={24} />}
            loading={updatingCharacteristic === 'onOffMode'}
            disabled={isUpdating && updatingCharacteristic !== 'onOffMode'}
            onClick={() => {
              const newIsOn = !isOn;
              setIsOn(newIsOn);
              setCharacteristic('onOffMode', newIsOn ? 'on' : 'off');
            }}
            style={{
              fontSize: '16px',
              backgroundColor: isOn ? 'black' : undefined, // čierne pozadie, keď je zapnuté
              color: isOn ? 'white' : undefined, // biele písmo, keď je zapnuté
              '&:hover': {
                backgroundColor: isOn ? '#333' : undefined, // tmavý hover, keď je zapnuté
              },
            }}
          >
            {isOn ? _t('Turn off') : _t('Turn on')}
          </Button>

          <ActionIcon size={60} radius="xl" variant="secondary" onClick={increaseTemp} disabled={!isOn || isUpdating}>
            <IconPlus size={28} />
          </ActionIcon>
        </Flex>
      </Paper>
      <Paper style={{ borderRadius: '0px 0px 10px 10px ' }} py="20px" px="16px" w="100%" bg="white">
        {/* MODES */}
        <Grid gutter="12px">
          <Grid.Col span={6}>
            <Button
              fullWidth
              radius="10px"
              variant={mode === 'cool' ? 'secondary' : 'secondary'}
              leftSection={<IconBrandSnowflake size={18} />}
              loading={updatingCharacteristic === 'operationMode' && mode === 'cool'}
              disabled={!isOn || (isUpdating && !(updatingCharacteristic === 'operationMode' && mode === 'cool'))}
              onClick={() => {
                setMode('cool');
                setCharacteristic('operationMode', 'cooling');
              }}
              size="xl"
              style={{
                fontSize: '16px',
                backgroundColor: mode === 'cool' && isOn ? 'black' : undefined,
                color: mode === 'cool' && isOn ? 'white' : undefined,
              }}
            >
              {_t('Cooling')}
            </Button>
          </Grid.Col>

          <Grid.Col span={6}>
            <Button
              fullWidth
              radius="10px"
              variant={mode === 'heat' ? 'secondary' : 'secondary'}
              leftSection={<IconSun size={18} />}
              loading={updatingCharacteristic === 'operationMode' && mode === 'heat'}
              disabled={!isOn || (isUpdating && !(updatingCharacteristic === 'operationMode' && mode === 'heat'))}
              onClick={() => {
                setMode('heat');
                setCharacteristic('operationMode', 'heating');
              }}
              size="xl"
              style={{
                fontSize: '16px',
                backgroundColor: mode === 'heat' && isOn ? 'black' : undefined,
                color: mode === 'heat' && isOn ? 'white' : undefined,
              }}
            >
              {_t('Heating')}
            </Button>
          </Grid.Col>

          <Grid.Col span={12}>
            <Button
              fullWidth
              radius="10px"
              variant={mode === 'fan' ? 'secondary' : 'secondary'}
              leftSection={<IconPropeller size={18} />}
              loading={updatingCharacteristic === 'operationMode' && mode === 'fan'}
              disabled={!isOn || (isUpdating && !(updatingCharacteristic === 'operationMode' && mode === 'fan'))}
              onClick={() => {
                setMode('fan');
                setCharacteristic('operationMode', 'fanOnly');
              }}
              size="xl"
              style={{
                fontSize: '16px',
                backgroundColor: mode === 'fan' && isOn ? 'black' : undefined,
                color: mode === 'fan' && isOn ? 'white' : undefined,
              }}
            >
              {_t('Ventilation')}
            </Button>
          </Grid.Col>
          {/* FAN SPEED BUTTONS */}
          {mode === 'fan' && isOn && (
            <>
              <Grid.Col span={4}>
                <Button
                  fullWidth
                  radius="10px"
                  size="md"
                  variant="secondary"
                  onClick={() => setFanSpeed('low')}
                  style={{
                    backgroundColor: fanSpeed === 'low' ? 'black' : undefined,
                    color: fanSpeed === 'low' ? 'white' : undefined,
                  }}
                >
                  {_t('Low')}
                </Button>
              </Grid.Col>
              <Grid.Col span={4}>
                <Button
                  fullWidth
                  radius="10px"
                  size="md"
                  variant="secondary"
                  onClick={() => setFanSpeed('medium')}
                  style={{
                    backgroundColor: fanSpeed === 'medium' ? 'black' : undefined,
                    color: fanSpeed === 'medium' ? 'white' : undefined,
                  }}
                >
                  {_t('Medium')}
                </Button>
              </Grid.Col>
              <Grid.Col span={4}>
                <Button
                  fullWidth
                  radius="10px"
                  size="md"
                  variant="secondary"
                  onClick={() => setFanSpeed('high')}
                  style={{
                    backgroundColor: fanSpeed === 'high' ? 'black' : undefined,
                    color: fanSpeed === 'high' ? 'white' : undefined,
                  }}
                >
                  {_t('High')}
                </Button>
              </Grid.Col>
            </>
          )}
        </Grid>
      </Paper>
    </Stack>
  );
}

/**
 * Air Conditioner detail page
 */
export default function AirConditionerDetail() {
  const { _t } = useLang();
  const navigate = useNavigate();
  const { airConditionerId: airConditionerIdRaw = '' } = useParams();

  const airConditionerId = useMemo(() => {
    const parsedId = parseInt(airConditionerIdRaw);
    return parsedId;
  }, [airConditionerIdRaw]);

  if (!airConditionerId || isNaN(airConditionerId)) {
    return (
      <Center style={{ height: '100vh' }}>
        <Stack align="center" gap={16}>
          <H2SemiBold>{_t('Air Conditioner not found')}</H2SemiBold>

          <Button variant="primary" size="lg" onClick={() => navigate(DASHBOARD_PATH.original)}>
            {_t('Back')}
          </Button>
        </Stack>
      </Center>
    );
  }

  return <AirConditionerDetailImpl airConditionerId={airConditionerId} />;
}
